import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminInvoiceEditModal from "@/pages/admin/AdminInvoiceEditModal";
import AdminStorage from "@/pages/admin/AdminStorage";
import { supportCode, matchesSupportCode } from "@/lib/supportCode";
import {
  Activity, Users, Building2, FileText, Cable, Settings, Trash2,
  Search, RefreshCw, ShieldCheck, ExternalLink, Send, XCircle,
  ArrowRight, ArrowLeft, Loader2, KeyRound, Copy, LogOut, UserCog, Pencil, X,
  ShoppingBag, Tag, Coins, Plus, Minus, CheckCircle2, Receipt,
  AlertTriangle, PlayCircle, Server, Globe, Eye, EyeOff, Webhook, ChevronDown,
  HardDrive,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin panel — one-file structure with tab routing under /app/admin/*.
 * Only accessible if useAuth().user.is_admin === true.
 */
export default function AdminPanel() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/app" replace />;
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AdminHeader />
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 p-6 lg:p-10 min-w-0" data-testid="admin-main">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="sdi" element={<AdminSdi />} />
            <Route path="storage" element={<AdminStorage />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AdminHeader() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const doLogout = () => {
    logout();
    // Clear any stale impersonation state on logout
    try { localStorage.removeItem("impersonation_return"); } catch { /* ignore */ }
    navigate("/", { replace: true });
  };
  return (
    <div className="h-14 bg-[#0A0A0A] text-white flex items-center px-6 gap-4">
      <ShieldCheck className="w-4 h-4 text-emerald-400" />
      <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-bold">Admin console</div>
      <div className="ml-auto flex items-center gap-4">
        <button
          onClick={() => navigate("/app")}
          data-testid="admin-back-app"
          className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
        >
          <ArrowLeft className="w-3 h-3" /> Torna all&apos;app
        </button>
        <div className="h-4 w-px bg-white/15" />
        <button
          onClick={doLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-md border border-white/10"
        >
          <LogOut className="w-3 h-3" /> Esci
        </button>
      </div>
    </div>
  );
}

const NAV = [
  { to: "/app/admin", label: "Overview", icon: Activity, end: true },
  { to: "/app/admin/users", label: "Utenti", icon: Users },
  { to: "/app/admin/companies", label: "Aziende", icon: Building2 },
  { to: "/app/admin/invoices", label: "Fatture", icon: FileText },
  { to: "/app/admin/orders", label: "Ordini", icon: ShoppingBag },
  { to: "/app/admin/billing", label: "Fatturazione", icon: Receipt },
  { to: "/app/admin/discounts", label: "Sconti", icon: Tag },
  { to: "/app/admin/sdi", label: "SDI · OpenAPI", icon: Cable },
  { to: "/app/admin/storage", label: "Storage", icon: HardDrive },
  { to: "/app/admin/system", label: "Sistema", icon: Settings },
];

// Page size for every admin list (users / companies / invoices / SDI registries).
const ADMIN_PAGE_SIZE = 15;

/**
 * Generic edit modal used by Users / Companies / Invoices tabs.
 * `fields` is an array of {key, label, type?, options?, mono?, full?, hint?}.
 * `values` is the initial record. `onSave(form)` is called with the edited dict.
 */
function EditRecordModal({ open, onClose, title, subtitle, fields, values, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(values || {});
  }, [open, values]);
  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="admin-edit-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-display font-bold text-xl">{title}</h3>
            {subtitle && <p className="text-xs text-[#737373] mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md" data-testid="admin-edit-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <label className="block text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-1.5">{f.label}</label>
              {f.type === "select" ? (
                <select
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value || null)}
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm"
                  data-testid={`admin-edit-field-${f.key}`}
                >
                  <option value="">—</option>
                  {(f.options || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                  data-testid={`admin-edit-field-${f.key}`}
                />
              ) : f.type === "checkbox" ? (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form[f.key]}
                    onChange={(e) => setField(f.key, e.target.checked)}
                    data-testid={`admin-edit-field-${f.key}`}
                  />
                  <span>{f.hint || "Attivo"}</span>
                </label>
              ) : (
                <input
                  type={f.type || "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                  className={`w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm ${f.mono ? "font-mono" : ""}`}
                  data-testid={`admin-edit-field-${f.key}`}
                />
              )}
              {f.hint && f.type !== "checkbox" && (
                <p className="text-[10px] text-[#737373] mt-1">{f.hint}</p>
              )}
            </div>
          ))}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Annulla</button>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-edit-modal-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
            Salva
          </button>
        </div>
      </form>
    </div>
  );
}

// -------- Impersonation flow --------
// Save the CURRENT admin session, swap token+user for the impersonated user,
// then hard-reload to /app so all contexts re-init cleanly.
async function startImpersonation(userId) {
  const currentToken = localStorage.getItem("auth_token");
  const currentUser = localStorage.getItem("auth_user");
  const { data } = await api.post(`/admin/users/${userId}/impersonate`);
  try {
    localStorage.setItem(
      "impersonation_return",
      JSON.stringify({ token: currentToken, user: currentUser ? JSON.parse(currentUser) : null })
    );
    localStorage.setItem("auth_token", data.token);
    localStorage.setItem("auth_user", JSON.stringify(data.user));
  } catch (_) { /* ignore */ }
  window.location.href = "/app";
}

function Pager({ page, setPage, total, pageSize }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-[#4A4A4A]" data-testid="admin-pager">
      <div>{start}–{end} su {total}</div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          data-testid="admin-pager-prev"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
        >
          <ArrowLeft className="w-3 h-3" /> Precedente
        </button>
        <span className="tabular-nums font-semibold text-[#0A0A0A]">Pag. {page + 1} / {pages}</span>
        <button
          onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
          disabled={page + 1 >= pages}
          data-testid="admin-pager-next"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
        >
          Successiva <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function AdminSidebar() {
  return (
    <aside className="w-52 shrink-0 bg-white border-r border-[#E8E4D9] min-h-[calc(100vh-3.5rem)] p-4" data-testid="admin-sidebar">
      <nav className="flex flex-col gap-1">
        {NAV.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]/g,"-")}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  isActive
                    ? "bg-[#0A0A0A] text-white font-semibold"
                    : "text-[#4A4A4A] hover:bg-[#F5F2EA]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

// -------------------- OVERVIEW --------------------
function AdminOverview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-sm text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>;
  const kpi = [
    { label: "Utenti", value: data.counts.users, icon: Users },
    { label: "di cui admin", value: data.counts.admins, icon: ShieldCheck },
    { label: "Aziende", value: data.counts.companies, icon: Building2 },
    { label: "Fatture", value: data.counts.invoices, icon: FileText },
    { label: "Inviate SDI", value: data.counts.invoices_sent_sdi, icon: Send },
    { label: "Trasm. manuali", value: data.counts.invoices_manual, icon: FileText },
    { label: "Scartate SDI", value: data.counts.invoices_rejected, icon: XCircle },
    { label: "FIC collegati", value: data.counts.fic_connections, icon: Cable },
    { label: "Inbound email", value: data.counts.inbound_emails, icon: FileText },
  ];
  return (
    <div data-testid="admin-overview">
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-[#0A0A0A]">Panoramica</h1>
      <p className="text-sm text-[#4A4A4A] mt-1">KPI globali della piattaforma.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-6" data-testid="admin-kpi-grid">
        {kpi.map((k) => (
          <div key={k.label} className="rounded-2xl border border-[#E8E4D9] bg-white p-4">
            <div className="w-8 h-8 rounded-lg bg-[#F5F2EA] grid place-items-center">
              <k.icon className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <div className="mt-3 text-[9px] uppercase tracking-[0.2em] text-[#737373] font-bold">{k.label}</div>
            <div className="font-display font-extrabold text-3xl tracking-tighter text-[#0A0A0A] tabular-nums">{k.value}</div>
          </div>
        ))}
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 col-span-2">
          <div className="text-[9px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Volume totale fatture</div>
          <div className="font-display font-extrabold text-3xl tracking-tighter text-emerald-900 tabular-nums">€ {Number(data.total_amount_eur).toFixed(2)}</div>
        </div>
      </div>

      <h2 className="font-display font-bold text-xl mt-8 mb-3">Ultimi 5 iscritti</h2>
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5">Iscritto</th>
              <th className="text-left px-4 py-2.5 w-24">Ruolo</th>
            </tr>
          </thead>
          <tbody>
            {data.latest_users.map((u, i) => (
              <tr key={u.id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""}>
                <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-2.5">{u.full_name}</td>
                <td className="px-4 py-2.5 text-[#737373] text-xs">{String(u.created_at).slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2.5">
                  {u.is_admin
                    ? <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Admin</span>
                    : <span className="text-[10px] text-[#737373] uppercase tracking-wider">Utente</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- USERS --------------------
function AdminUsers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwPopup, setPwPopup] = useState(null);
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState(null);
  const [confirmImperson, setConfirmImperson] = useState(null);
  const [editCredits, setEditCredits] = useState(null); // {user, delta}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setItems(data);
      setPage(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const paged = useMemo(
    () => items.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE),
    [items, page]
  );

  const toggleAdmin = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
    await load();
    toast.success(u.is_admin ? "Rimosso ruolo admin" : "Promosso ad admin");
  };
  const toggleDisable = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { is_disabled: !u.is_disabled });
    await load();
    toast.success(u.is_disabled ? "Riattivato" : "Disabilitato");
  };
  const resetPw = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    setPwPopup({ email: u.email, pw: data.temporary_password });
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    await api.delete(`/admin/users/${confirmDel.id}`);
    setConfirmDel(null);
    await load();
    toast.success("Utente eliminato");
  };
  const saveEdit = async (form) => {
    // Ricompone billing_info dai campi flat bi_*. Preserva quelli non toccati
    // fondendo con quelli originali già presenti.
    const billing_info = { ...(editing?.billing_info || {}) };
    Object.keys(form).forEach((k) => {
      if (k.startsWith("bi_")) billing_info[k.slice(3)] = form[k] ?? "";
    });
    const patch = {
      email: form.email,
      full_name: form.full_name,
      nome: form.nome,
      cognome: form.cognome,
      referral_code: form.referral_code,
      inbound_email: form.inbound_email,
      is_commercialista: !!form.is_commercialista,
      email_verified: !!form.email_verified,
      is_admin: !!form.is_admin,
      billing_info,
    };
    await api.patch(`/admin/users/${editing.id}`, patch);
    await load();
    toast.success("Utente aggiornato");
  };
  const doImpersonate = async () => {
    if (!confirmImperson) return;
    try {
      await startImpersonation(confirmImperson.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile impersonare");
      setConfirmImperson(null);
    }
  };
  const saveCredits = async () => {
    if (!editCredits) return;
    const delta = Number(editCredits.delta || 0);
    if (!delta) { toast.error("Inserisci un delta diverso da 0"); return; }
    try {
      const { data } = await api.post(`/admin/users/${editCredits.user.id}/add-credits`, { delta });
      toast.success(`${delta > 0 ? "+" : ""}${delta} crediti a ${data.email}. Saldo: ${data.credits_available}`);
      setEditCredits(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-display font-extrabold text-3xl tracking-tight">Utenti</h1>
      <p className="text-sm text-[#4A4A4A] mt-1">Gestione account: promuovi ad admin, disabilita, reset password, elimina.</p>

      <div className="mt-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Cerca email o nome…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm"
          />
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]" data-testid="admin-users-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
        </button>
      </div>

      <div className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5">P.IVA (azienda)</th>
              <th className="text-left px-4 py-2.5 w-20">Fatture</th>
              <th className="text-left px-4 py-2.5 w-20">Crediti</th>
              <th className="text-left px-4 py-2.5 w-24">Ruolo</th>
              <th className="text-left px-4 py-2.5 w-20">Attivo</th>
              <th className="text-right px-4 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#737373]">Caricamento…</td></tr>}
            {!loading && paged.map((u, i) => (
              <tr key={u.id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-user-row-${i}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-2.5">{u.full_name}</td>
                <td className="px-4 py-2.5 text-xs">
                  <div>{u.company?.denominazione || <span className="text-[#A3A3A3] italic">nessuna</span>}</div>
                  <div className="text-[#737373] font-mono">{u.company?.partita_iva || ""}</div>
                </td>
                <td className="px-4 py-2.5 tabular-nums">{u.invoices_count}</td>
                <td className="px-4 py-2.5 tabular-nums" data-testid={`admin-user-credits-${i}`}>
                  <button
                    onClick={() => setEditCredits({ user: u, delta: 25 })}
                    title="Modifica crediti"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold text-[#1D4ED8] hover:bg-blue-50 border border-blue-100"
                    data-testid={`admin-user-credits-btn-${i}`}
                  >
                    <Coins className="w-3 h-3" />
                    <span className="tabular-nums">{u.credits_available ?? 0}</span>
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <button onClick={() => toggleAdmin(u)} data-testid={`admin-toggle-admin-${i}`} className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${u.is_admin ? "bg-emerald-100 text-emerald-700" : "bg-[#F5F2EA] text-[#4A4A4A]"}`}>
                    {u.is_admin ? "Admin" : "Utente"}
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <button onClick={() => toggleDisable(u)} data-testid={`admin-toggle-active-${i}`} className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${u.is_disabled ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {u.is_disabled ? "Off" : "On"}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => setConfirmImperson(u)}
                    title="Impersona questo utente"
                    data-testid={`admin-user-impersonate-${i}`}
                    className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#1D4ED8] hover:bg-blue-50"
                  >
                    <UserCog className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => {
                      // Pre-flatten billing_info in campi flat bi_* così il
                      // modal (che è flat) può visualizzarli in input singoli.
                      const bi = u.billing_info || {};
                      const flat = Object.fromEntries(
                        Object.entries(bi).map(([k, v]) => [`bi_${k}`, v ?? ""])
                      );
                      setEditing({ ...u, ...flat });
                    }}
                    title="Modifica"
                    data-testid={`admin-user-edit-${i}`}
                    className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#0A0A0A] hover:bg-[#F5F2EA]"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => resetPw(u)} title="Reset password" data-testid={`admin-user-resetpw-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#0A0A0A] hover:bg-[#F5F2EA]">
                    <KeyRound className="w-3 h-3" />
                  </button>
                  <button onClick={() => setConfirmDel(u)} title="Elimina" data-testid={`admin-user-delete-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={page} setPage={setPage} total={items.length} pageSize={ADMIN_PAGE_SIZE} />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        icon={Trash2}
        tone="danger"
        title={`Eliminare ${confirmDel?.email}?`}
        description="Elimina utente + azienda + fatture + connessioni FIC + audit inbound. Operazione irreversibile."
        confirmLabel="Sì, elimina"
      />

      <ConfirmDialog
        open={!!confirmImperson}
        onOpenChange={(v) => !v && setConfirmImperson(null)}
        onConfirm={doImpersonate}
        icon={UserCog}
        tone="primary"
        title={`Impersonare ${confirmImperson?.email}?`}
        description="Verrai loggato come questo utente. Vedrai la piattaforma dai suoi occhi, con la sua azienda e le sue fatture. Potrai tornare al pannello admin in qualsiasi momento tramite il banner rosso in alto."
        confirmLabel="Sì, impersona"
      />

      <EditRecordModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Modifica utente ${editing?.email || ""}`}
        subtitle="Puoi cambiare qualsiasi campo. Le modifiche sono immediate."
        values={editing || {}}
        fields={[
          { key: "email", label: "Email", type: "email", mono: true },
          { key: "full_name", label: "Nome completo" },
          { key: "nome", label: "Nome" },
          { key: "cognome", label: "Cognome" },
          { key: "referral_code", label: "Codice referral", mono: true },
          { key: "inbound_email", label: "Inbound email", mono: true, full: true, hint: "Indirizzo email dedicato per ricezione fatture (Resend)" },
          { key: "is_commercialista", label: "Profilo Commercialista", type: "checkbox", hint: "Consente all'utente di gestire pi\u00f9 aziende clienti" },
          { key: "email_verified", label: "Email verificata", type: "checkbox", hint: "Marca l'indirizzo come confermato (bypass link di verifica)" },
          { key: "is_admin", label: "Amministratore", type: "checkbox", hint: "Concede accesso al pannello admin" },
          // ─── Dati fiscali (billing_info) — usati per emettere la fattura di vendita al checkout crediti ───
          { key: "bi_denominazione", label: "Ragione sociale / Denominazione", full: true, hint: "Come apparirà in fattura (Denominazione XML SDI)" },
          { key: "bi_partita_iva", label: "Partita IVA (11 cifre)", mono: true, hint: "Obbligatoria per B2B italiano" },
          { key: "bi_codice_fiscale", label: "Codice fiscale (11 o 16 char)", mono: true, hint: "Per privati o legale rappresentante" },
          { key: "bi_indirizzo", label: "Indirizzo (via + numero civico)", full: true },
          { key: "bi_cap", label: "CAP (5 cifre)", mono: true },
          { key: "bi_comune", label: "Comune" },
          { key: "bi_provincia", label: "Provincia (2 lettere)", mono: true },
          { key: "bi_nazione", label: "Nazione (ISO2, default IT)", mono: true },
          { key: "bi_codice_destinatario", label: "Codice destinatario SDI (7 char, 0000000 di default)", mono: true },
          { key: "bi_email_pec", label: "PEC", mono: true, full: true, hint: "Per notifiche SDI quando Codice destinatario = 0000000" },
        ]}
        onSave={saveEdit}
      />

      {editCredits && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setEditCredits(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="admin-credits-modal">
            <div className="p-6 bg-gradient-to-br from-[#0A0A0A] to-[#1D4ED8] text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 mb-2">
                <Coins className="w-3 h-3" /> Gestione crediti
              </div>
              <h3 className="font-display font-extrabold text-2xl tracking-tighter">
                {editCredits.user.email}
              </h3>
              <div className="mt-2 text-sm text-white/80">
                Saldo attuale: <strong className="font-mono text-white tabular-nums">{editCredits.user.credits_available ?? 0}</strong> crediti
              </div>
            </div>
            <div className="p-6">
              <label className="block text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-2">
                Delta crediti (positivo = aggiungi · negativo = sottrai)
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditCredits({ ...editCredits, delta: Number(editCredits.delta || 0) - 5 })}
                  className="p-2 rounded-md border border-[#E8E4D9] bg-white hover:bg-[#F5F2EA]"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={editCredits.delta}
                  onChange={(e) => setEditCredits({ ...editCredits, delta: e.target.value })}
                  data-testid="admin-credits-delta-input"
                  className="flex-1 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-center text-lg font-bold tabular-nums"
                />
                <button
                  type="button"
                  onClick={() => setEditCredits({ ...editCredits, delta: Number(editCredits.delta || 0) + 5 })}
                  className="p-2 rounded-md border border-[#E8E4D9] bg-white hover:bg-[#F5F2EA]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {[5, 25, 60, 150].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setEditCredits({ ...editCredits, delta: v })}
                    className="px-2 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA]"
                  >
                    +{v}
                  </button>
                ))}
              </div>
              <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-[#1D4ED8]">
                Nuovo saldo dopo l&apos;operazione: <strong className="tabular-nums font-mono">{(editCredits.user.credits_available ?? 0) + Number(editCredits.delta || 0)}</strong> crediti.
                {Number(editCredits.delta) > 0 && " Eventuali fatture in coda verranno riprocessate automaticamente."}
              </div>
            </div>
            <div className="border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setEditCredits(null)} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm">Annulla</button>
              <button
                type="button"
                onClick={saveCredits}
                data-testid="admin-credits-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8]"
              >
                <Coins className="w-3.5 h-3.5" /> Applica {editCredits.delta > 0 ? "+" : ""}{editCredits.delta}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwPopup && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center" onClick={() => setPwPopup(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-xl">Password temporanea generata</h3>
            <p className="text-sm text-[#4A4A4A] mt-1">Comunica manualmente questa password all&apos;utente {pwPopup.email}. Non sarà più mostrata.</p>
            <div className="mt-4 p-3 rounded-lg bg-[#F5F2EA] font-mono text-sm break-all">{pwPopup.pw}</div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => { navigator.clipboard.writeText(pwPopup.pw); toast.success("Copiata"); }} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">
                <Copy className="w-3 h-3" /> Copia
              </button>
              <button onClick={() => setPwPopup(null)} className="px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- COMPANIES --------------------
function AdminCompanies() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/companies");
    setItems(r.data);
    setPage(0);
  };
  useEffect(() => { load(); }, []);

  const paged = useMemo(
    () => items.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE),
    [items, page]
  );

  const saveEdit = async (form) => {
    // Send only whitelisted top-level fields
    const KEYS = ["denominazione", "partita_iva", "codice_fiscale", "regime_fiscale",
      "indirizzo", "cap", "comune", "provincia", "nazione", "email_pec",
      "codice_destinatario", "sezionale", "inbound_email",
      "aliquota_iva_default", "is_active"];
    const patch = {};
    KEYS.forEach((k) => { if (form[k] !== undefined) patch[k] = form[k]; });
    await api.patch(`/admin/companies/${editing.id}`, patch);
    await load();
    toast.success("Azienda aggiornata");
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    await api.delete(`/admin/companies/${confirmDel.id}`);
    setConfirmDel(null);
    await load();
    toast.success("Azienda eliminata");
  };

  return (
    <div data-testid="admin-companies">
      <h1 className="font-display font-extrabold text-3xl tracking-tight">Aziende</h1>
      <p className="text-sm text-[#4A4A4A] mt-1">Tutte le aziende registrate sulla piattaforma. {items.length} totali.</p>
      <div className="mt-6 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Denominazione</th>
              <th className="text-left px-4 py-2.5">P.IVA</th>
              <th className="text-left px-4 py-2.5">Utente</th>
              <th className="text-left px-4 py-2.5">Inbound email</th>
              <th className="text-left px-4 py-2.5 w-20">Fatture</th>
              <th className="text-left px-4 py-2.5 w-20">Sezionale</th>
              <th className="text-right px-4 py-2.5 w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((c, i) => (
              <tr key={c.id || `${c.user_id}-${i}`} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-company-row-${i}`}>
                <td className="px-4 py-2.5 font-medium">{c.denominazione}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{c.partita_iva}</td>
                <td className="px-4 py-2.5 text-xs">{c.user?.email || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-[#737373]">{c.inbound_email}</td>
                <td className="px-4 py-2.5 tabular-nums">{c.invoices_count}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{c.sezionale}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => setEditing(c)} title="Modifica" data-testid={`admin-company-edit-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#0A0A0A] hover:bg-[#F5F2EA]">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => setConfirmDel(c)} title="Elimina" data-testid={`admin-company-delete-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} setPage={setPage} total={items.length} pageSize={ADMIN_PAGE_SIZE} />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        icon={Trash2}
        tone="danger"
        title={`Eliminare azienda ${confirmDel?.denominazione}?`}
        description="Rimuove il profilo azienda. Le fatture e l'utente rimangono, ma perdono il collegamento fiscale."
        confirmLabel="Sì, elimina"
      />

      <EditRecordModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Modifica azienda ${editing?.denominazione || ""}`}
        subtitle="Ogni campo qui presente sovrascrive quello del profilo aziendale dell'utente."
        values={editing || {}}
        fields={[
          { key: "denominazione", label: "Denominazione", full: true },
          { key: "partita_iva", label: "P.IVA", mono: true },
          { key: "codice_fiscale", label: "Codice fiscale", mono: true },
          { key: "regime_fiscale", label: "Regime fiscale", type: "select", options: [
            { value: "RF01", label: "RF01 - Ordinario" },
            { value: "RF02", label: "RF02 - Minimi" },
            { value: "RF19", label: "RF19 - Forfettario" },
          ]},
          { key: "codice_destinatario", label: "Codice destinatario SDI", mono: true },
          { key: "email_pec", label: "Email PEC", mono: true },
          { key: "indirizzo", label: "Indirizzo", full: true },
          { key: "cap", label: "CAP", mono: true },
          { key: "comune", label: "Comune" },
          { key: "provincia", label: "Provincia" },
          { key: "nazione", label: "Nazione (ISO 2)", mono: true },
          { key: "sezionale", label: "Sezionale numerazione", mono: true },
          { key: "aliquota_iva_default", label: "Aliquota IVA default (%)", type: "number" },
          { key: "inbound_email", label: "Inbound email", mono: true, full: true },
        ]}
        onSave={saveEdit}
      />
    </div>
  );
}

// -------------------- INVOICES --------------------
function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sdiFilter, setSdiFilter] = useState("");
  const [query, setQuery] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (sdiFilter) params.sdi_status = sdiFilter;
    const { data } = await api.get("/admin/invoices", { params });
    setItems(data);
    setPage(0);
  };
  useEffect(() => { load(); }, [statusFilter, sdiFilter]);

  // Client-side search on top of server filters. Matches: codice supporto
  // (AF-XXXXXX), autofattura_number, supplier name, user email, invoice id
  // prefix. Case-insensitive.
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      return (
        matchesSupportCode(d.id, q) ||
        (d.extracted?.autofattura_number || "").toLowerCase().includes(q) ||
        (d.extracted?.supplier_name || "").toLowerCase().includes(q) ||
        (d.user?.email || "").toLowerCase().includes(q) ||
        (d.id || "").toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  const paged = useMemo(
    () => filteredItems.slice(page * ADMIN_PAGE_SIZE, (page + 1) * ADMIN_PAGE_SIZE),
    [filteredItems, page]
  );
  useEffect(() => { setPage(0); }, [query]);

  const openEdit = (d) => {
    // Full invoice passed as-is to the new modal — it uses the same section
    // components as the user's InvoiceReview page so all fields are populated
    // exactly the way the user sees them.
    setEditing(d);
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const { data } = await api.delete(`/admin/invoices/${confirmDel.id}`);
    setConfirmDel(null);
    await load();
    const refunded = data?.credits_refunded || 0;
    const rb = data?.af_rollback;
    const parts = [];
    if (refunded > 0) parts.push(`${refunded} credito rimborsato`);
    if (rb) parts.push(`progressivo rimesso a ${rb.to} (anno ${rb.year})`);
    if (parts.length > 0) {
      toast.success(`Fattura eliminata · ${parts.join(" · ")}`);
    } else {
      toast.success("Fattura eliminata");
    }
  };

  return (
    <div data-testid="admin-invoices">
      <h1 className="font-display font-extrabold text-3xl tracking-tight">Fatture globali</h1>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per codice AF-XXXXXX, numero, fornitore, email…"
          data-testid="admin-invoices-search"
          className="flex-1 min-w-[280px] px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" data-testid="admin-invoices-status-filter">
          <option value="">Tutti gli stati</option>
          <option value="uploaded">Caricata</option>
          <option value="extracting">In estrazione</option>
          <option value="extracted">Estratta</option>
          <option value="xml_generated">XML pronto</option>
          <option value="error">Errore</option>
        </select>
        <select value={sdiFilter} onChange={(e) => setSdiFilter(e.target.value)} className="px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" data-testid="admin-invoices-sdi-filter">
          <option value="">Tutti SDI</option>
          <option value="RC">Consegnate</option>
          <option value="NS">Scartate</option>
          <option value="MC">Mancata consegna</option>
          <option value="DT">Termini decorsi</option>
        </select>
        <div className="text-sm text-[#737373]">
          {filteredItems.length} {filteredItems.length === 1 ? "risultato" : "risultati"}
          {query && filteredItems.length !== items.length && (
            <span className="text-[#A3A3A3]"> · su {items.length}</span>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Codice supporto</th>
              <th className="text-left px-4 py-2.5">Autofattura</th>
              <th className="text-left px-4 py-2.5">Utente</th>
              <th className="text-left px-4 py-2.5">Fornitore</th>
              <th className="text-left px-4 py-2.5 w-20">Status</th>
              <th className="text-left px-4 py-2.5 w-20">SDI</th>
              <th className="text-right px-4 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((d, i) => (
              <tr key={d.id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-invoice-row-${i}`}>
                <td className="px-4 py-2.5 font-mono font-bold text-xs text-[#1D4ED8]" title={d.id} data-testid={`admin-invoice-support-code-${i}`}>
                  {supportCode(d.id)}
                </td>
                <td className="px-4 py-2.5 font-mono font-bold text-xs">{d.extracted?.autofattura_number || <span className="text-[#A3A3A3] italic font-normal">non assegnato</span>}</td>
                <td className="px-4 py-2.5 text-xs">{d.user?.email || "—"}</td>
                <td className="px-4 py-2.5">{d.extracted?.supplier_name || d.file_name}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider">{d.status}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider">{d.sdi_status || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => openEdit(d)} title="Modifica" data-testid={`admin-invoice-edit-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#0A0A0A] hover:bg-[#F5F2EA]">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => setConfirmDel(d)} data-testid={`admin-invoice-delete-${i}`} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={page} setPage={setPage} total={filteredItems.length} pageSize={ADMIN_PAGE_SIZE} />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        icon={Trash2}
        tone="danger"
        title="Eliminare la fattura?"
        description={
          confirmDel?.credit_charged
            ? "Rimuove il documento e i file fisici (PDF/XML) associati. All'utente verrà rimborsato automaticamente 1 credito (audit trail salvato). Operazione irreversibile."
            : "Rimuove il documento e i file fisici (PDF/XML) associati. Nessun credito da rimborsare (fattura mai addebitata). Operazione irreversibile."
        }
        confirmLabel="Sì, elimina"
      />

      <AdminInvoiceEditModal
        open={!!editing}
        invoice={editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}

// -------------------- SDI --------------------
function AdminSdi() {
  const [wallet, setWallet] = useState(null);
  const [registries, setRegistries] = useState([]);
  const [confirmDelReg, setConfirmDelReg] = useState(null);
  const [newReg, setNewReg] = useState({ fiscal_id: "", name: "", email: "" });
  const [regPage, setRegPage] = useState(0);

  const load = async () => {
    try {
      const w = await api.get("/admin/sdi/wallet");
      setWallet(w.data);
    } catch { setWallet(null); }
    try {
      const r = await api.get("/admin/sdi/registries");
      setRegistries((r.data?.data) || []);
    } catch { setRegistries([]); }
  };
  useEffect(() => { load(); }, []);

  const createReg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/sdi/registries", newReg);
      setNewReg({ fiscal_id: "", name: "", email: "" });
      toast.success("Registry creato");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };
  const deleteReg = async () => {
    if (!confirmDelReg) return;
    try {
      await api.delete(`/admin/sdi/registries/${confirmDelReg.fiscal_id}`);
      toast.success("Registry cancellato");
      setConfirmDelReg(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };
  const activateReg = async (fid) => {
    try {
      await api.patch(`/admin/sdi/registries/${fid}/activate`);
      toast.success("Attivato");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="admin-sdi">
      <h1 className="font-display font-extrabold text-3xl tracking-tight">SDI · OpenAPI</h1>

      {wallet && (
        <div className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white p-4 flex items-center justify-between">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#737373] font-bold">Credito · {wallet.mode}</div>
            <div className="font-display font-extrabold text-3xl tracking-tighter">€ {Number(wallet.credit_eur).toFixed(2)}</div>
          </div>
          <a href="https://console.openapi.com/apis/sdi/subscription" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">
            Ricarica <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      <h2 className="font-display font-bold text-xl mt-8 mb-3">Business Registry ({registries.length})</h2>
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Fiscal ID</th>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5 w-24">Attivo</th>
              <th className="text-left px-4 py-2.5 w-24">Firma</th>
              <th className="text-right px-4 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {registries.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#737373] text-xs">Nessun registry configurato.</td></tr>}
            {registries.slice(regPage * ADMIN_PAGE_SIZE, (regPage + 1) * ADMIN_PAGE_SIZE).map((r, i) => (
              <tr key={r.fiscal_id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-registry-row-${i}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{r.fiscal_id}</td>
                <td className="px-4 py-2.5">{r.name}</td>
                <td className="px-4 py-2.5 text-xs">{r.email}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider">{r.active ? "Sì" : "No"}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider">{r.apply_signature ? "Sì" : "No"}</td>
                <td className="px-4 py-2.5 text-right space-x-1">
                  {!r.active && (
                    <button onClick={() => activateReg(r.fiscal_id)} className="text-[10px] font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Attiva</button>
                  )}
                  <button onClick={() => setConfirmDelReg(r)} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={regPage} setPage={setRegPage} total={registries.length} pageSize={ADMIN_PAGE_SIZE} />

      <form onSubmit={createReg} className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white p-4 flex flex-wrap items-end gap-2" data-testid="admin-registry-create">
        <div className="flex-1 min-w-[140px]">
          <div className="text-[9px] uppercase tracking-wider text-[#737373] font-bold">P.IVA</div>
          <input value={newReg.fiscal_id} onChange={(e) => setNewReg({ ...newReg, fiscal_id: e.target.value })} required className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono" />
        </div>
        <div className="flex-1 min-w-[140px]">
          <div className="text-[9px] uppercase tracking-wider text-[#737373] font-bold">Denominazione</div>
          <input value={newReg.name} onChange={(e) => setNewReg({ ...newReg, name: e.target.value })} required className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-[9px] uppercase tracking-wider text-[#737373] font-bold">Email</div>
          <input type="email" value={newReg.email} onChange={(e) => setNewReg({ ...newReg, email: e.target.value })} required className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" />
        </div>
        <button type="submit" className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8]">
          <ArrowRight className="w-3.5 h-3.5" /> Crea
        </button>
      </form>

      <ConfirmDialog
        open={!!confirmDelReg}
        onOpenChange={(v) => !v && setConfirmDelReg(null)}
        onConfirm={deleteReg}
        icon={Trash2}
        tone="danger"
        title={`Cancellare registry ${confirmDelReg?.fiscal_id}?`}
        description="Rimuove il business_registry_configuration da OpenAPI. Non modifica i dati locali."
        confirmLabel="Sì, cancella"
      />
    </div>
  );
}

// -------------------- SYSTEM --------------------
function AdminSystem() {
  const [slots, setSlots] = useState(null);
  const [groups, setGroups] = useState(null);
  const [conf, setConf] = useState(null);
  const [health, setHealth] = useState(null);
  const [envDiag, setEnvDiag] = useState(null);
  const [envEffective, setEnvEffective] = useState(null);
  const [providerKeys, setProviderKeys] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [diskUsage, setDiskUsage] = useState(null);
  const [cronLastRun, setCronLastRun] = useState(null);
  const [prodDomain, setProdDomain] = useState("autofattura.dasabo.com");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [testingAll, setTestingAll] = useState(false);
  const [confirmClear, setConfirmClear] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(null);

  const load = async () => {
    try {
      const [k, m, h, e, wh, du, cr] = await Promise.all([
        api.get("/admin/system/keys"),
        api.get("/admin/system/mode"),
        api.get("/admin/system/health"),
        api.get("/admin/system/env-diagnostics"),
        api.get("/admin/system/webhook-health").catch(() => ({ data: null })),
        api.get("/admin/system/disk-usage").catch(() => ({ data: null })),
        api.get("/admin/system/cron/last-run").catch(() => ({ data: null })),
      ]);
      setSlots(k.data.slots);
      setGroups(k.data.groups || {});
      setConf(m.data);
      setHealth(h.data);
      setEnvDiag(e.data.env);
      setEnvEffective(e.data.effective || null);
      setProviderKeys(e.data.provider_keys || null);
      setWebhookHealth(wh.data);
      setDiskUsage(du.data);
      setCronLastRun(cr.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento sistema");
    }
  };

  const loadWebhooks = async (domain) => {
    try {
      const { data } = await api.get(`/admin/system/webhook-endpoints${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`);
      setWebhooks(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento webhook");
    }
  };

  useEffect(() => { load(); loadWebhooks(prodDomain); }, []);

  const saveKey = async (slot, value) => {
    setBusy(slot);
    try {
      await api.put(`/admin/system/keys/${slot}`, { value });
      toast.success("Chiave salvata (cifrata a riposo). Consigliato lanciare 'Test' subito dopo.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(null);
    }
  };

  const clearKey = async (slotKey) => {
    setBusy(slotKey);
    try {
      await api.delete(`/admin/system/keys/${slotKey}`);
      toast.success("Chiave rimossa dal DB.");
      setConfirmClear(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione");
    } finally {
      setBusy(null);
    }
  };

  const testKey = async (slot) => {
    setBusy(slot);
    try {
      const { data } = await api.post(`/admin/system/keys/${slot}/test`);
      if (data.ok) toast.success(data.msg || "Connessione OK");
      else toast.error(data.msg || "Test fallito");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore test");
    } finally {
      setBusy(null);
    }
  };

  const testAll = async () => {
    setTestingAll(true);
    try {
      const { data } = await api.post("/admin/system/test-all");
      if (data.ko === 0) {
        toast.success(`Tutti i ${data.ok} test superati (${data.skipped} slot skippati)`);
      } else {
        toast.error(`${data.ko} test falliti su ${data.total}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore test batch");
    } finally {
      setTestingAll(false);
    }
  };

  if (!slots || !conf || !health) return <div className="text-sm text-[#737373]">Caricamento…</div>;

  return (
    <div data-testid="admin-system" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Sistema</h1>
          <p className="text-sm text-[#4A4A4A] mt-1 max-w-2xl">
            Chiavi API dei provider, health check pre-deploy, diagnostica ambiente e webhook endpoints.
            Le chiavi sono cifrate a riposo con Fernet + <span className="font-mono text-[11px]">SYSTEM_ENC_KEY</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={testAll} disabled={testingAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-bold disabled:opacity-40"
            data-testid="sys-test-all">
            {testingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Testa tutte le chiavi
          </button>
          <button onClick={() => { load(); loadWebhooks(prodDomain); }}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold"
            data-testid="sys-refresh">
            <RefreshCw className="w-3 h-3" /> Ricarica
          </button>
        </div>
      </div>

      {/* WARNING BANNER: SYSTEM_ENC_KEY non impostata (rischio prod) */}
      {health.encryption.level !== "strong" && (
        <div className="rounded-2xl border border-red-300 bg-red-50 p-5" data-testid="sys-enc-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-bold text-red-900">
                Encryption key {health.encryption.level === "invalid" ? "NON VALIDA" : "DERIVATA (rischio produzione)"}
              </div>
              <p className="text-xs text-red-800 mt-1 leading-relaxed whitespace-pre-line">
                {health.encryption.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HEALTH OVERVIEW */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="sys-health">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${health.prod_ready ? "bg-emerald-100" : "bg-amber-100"}`}>
              {health.prod_ready
                ? <ShieldCheck className="w-5 h-5 text-emerald-700" />
                : <AlertTriangle className="w-5 h-5 text-amber-700" />}
            </div>
            <div>
              <div className="font-display font-bold text-lg">
                {health.prod_ready ? "Pronto per la produzione" : "Configurazione da completare"}
              </div>
              <div className="text-xs text-[#737373] mt-0.5">
                {health.totals.in_db + health.totals.in_env}/{health.totals.all} chiavi configurate · {health.totals.tested_ok}/{health.totals.testable} test superati
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs font-semibold">
            <StatDot color="emerald" label={`${health.totals.in_db} in DB`} testid="sys-stat-db" />
            <StatDot color="amber" label={`${health.totals.in_env} ENV fallback`} testid="sys-stat-env" />
            <StatDot color="red" label={`${health.totals.missing} mancanti`} testid="sys-stat-missing" />
          </div>
        </div>

        {/* progress bar */}
        <div className="mt-4 h-2 rounded-full bg-[#F5F2EA] overflow-hidden flex">
          <div className="bg-emerald-500 h-full" style={{ width: `${(health.totals.in_db / health.totals.all) * 100}%` }} />
          <div className="bg-amber-400 h-full" style={{ width: `${(health.totals.in_env / health.totals.all) * 100}%` }} />
          <div className="bg-red-400 h-full" style={{ width: `${(health.totals.missing / health.totals.all) * 100}%` }} />
        </div>

        {/* warnings */}
        {health.warnings.length > 0 && (
          <ul className="mt-4 space-y-2">
            {health.warnings.map((w, i) => (
              <li key={`${w.severity}-${w.message}-${i}`} className={`text-xs px-3 py-2 rounded-md ${
                w.severity === "critical" ? "bg-red-50 text-red-800 border border-red-200"
                : w.severity === "warning" ? "bg-amber-50 text-amber-800 border border-amber-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}>
                {w.message}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* WEBHOOK HEALTH (v17e) — liveness signal del canale push OpenAPI */}
      {webhookHealth && (
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="sys-webhook-health">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                webhookHealth.status === "healthy" ? "bg-emerald-100"
                : webhookHealth.status === "stale" ? "bg-amber-100"
                : webhookHealth.status === "critical" ? "bg-red-100"
                : "bg-zinc-100"
              }`}>
                {webhookHealth.status === "healthy" && <ShieldCheck className="w-5 h-5 text-emerald-700" />}
                {webhookHealth.status === "stale" && <AlertTriangle className="w-5 h-5 text-amber-700" />}
                {webhookHealth.status === "critical" && <AlertTriangle className="w-5 h-5 text-red-700" />}
                {webhookHealth.status === "unknown" && <RefreshCw className="w-5 h-5 text-zinc-500" />}
              </div>
              <div>
                <div className="font-display font-bold text-lg">
                  Webhook OpenAPI — {webhookHealth.status === "healthy" ? "attivo"
                    : webhookHealth.status === "stale" ? "in ritardo"
                    : webhookHealth.status === "critical" ? "silente da >24h"
                    : "in attesa del primo evento"}
                </div>
                <div className="text-xs text-[#737373] mt-0.5">
                  {webhookHealth.latest?.ts
                    ? <>Ultimo evento: {new Date(webhookHealth.latest.ts).toLocaleString("it-IT")} · UUID <span className="font-mono">{(webhookHealth.latest.invoice_uuid || "").slice(0, 8)}…</span></>
                    : "Nessun evento ricevuto ancora — atteso subito dopo il primo invio SDI post-deploy."}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <StatDot color="emerald" label={`${webhookHealth.counts_24h.total} eventi/24h`} testid="wh-total" />
              <StatDot color="blue" label={`${webhookHealth.counts_24h.matched} matched`} testid="wh-matched" />
              {webhookHealth.counts_24h.ns_rejections > 0 && (
                <StatDot color="red" label={`${webhookHealth.counts_24h.ns_rejections} scarti NS`} testid="wh-ns" />
              )}
            </div>
          </div>
          {webhookHealth.status === "critical" && (
            <p className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2 leading-relaxed">
              ⚠️ Nessun webhook nelle ultime 24 ore. Cause possibili: (a) nessuna fattura inviata in questo periodo, (b) OpenAPI non chiama più il nostro endpoint (secret cambiato / URL non registrato), (c) rete verso OpenAPI down. Verifica la sezione API Configurations del portale OpenAPI.
            </p>
          )}
        </div>
      )}

      {/* CHIAVI API — RAGGRUPPATE PER PROVIDER */}
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#0A0A0A]" />
          <h2 className="font-display font-bold text-xl">Chiavi API per provider</h2>
        </div>
        {Object.entries(groups || {}).map(([groupKey, meta]) => {
          const groupSlots = slots.filter((s) => s.provider_group === groupKey);
          if (groupSlots.length === 0) return null;
          const okCount = groupSlots.filter((s) => s.is_set).length;
          const testedOk = groupSlots.filter((s) => s.last_test_ok === true).length;
          const testable = groupSlots.filter((s) => s.test_provider).length;
          return (
            <div key={groupKey} className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden" data-testid={`sys-group-${groupKey}`}>
              <div className="px-5 py-4 border-b border-[#E8E4D9] flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base">{meta.label}</div>
                  <div className="text-xs text-[#737373] mt-0.5">{meta.description}</div>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
                  <span className="px-2 py-0.5 rounded-full bg-[#F5F2EA]">{okCount}/{groupSlots.length} configurate</span>
                  {testable > 0 && (
                    <span className={`px-2 py-0.5 rounded-full ${testedOk === testable ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {testedOk}/{testable} test ok
                    </span>
                  )}
                </div>
              </div>
              <div className="divide-y divide-[#E8E4D9]">
                {groupSlots.map((s) => (
                  <SystemSlotRow
                    key={s.key}
                    slot={s}
                    busy={busy === s.key}
                    isEditing={editing === s.key}
                    onEdit={() => setEditing(s.key)}
                    onCancel={() => setEditing(null)}
                    onSave={(v) => saveKey(s.key, v)}
                    onClear={() => setConfirmClear(s)}
                    onTest={() => testKey(s.key)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* OPENAPI TOKEN STATUS */}
      {conf?.mode && (
        <OpenApiTokenCard mode={conf.mode} />
      )}

      {/* WEBHOOK ENDPOINTS */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden" data-testid="sys-webhook-section">
        <div className="px-5 py-4 border-b border-[#E8E4D9] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Webhook className="w-4 h-4 text-[#0A0A0A]" />
            <h2 className="font-display font-bold text-lg">Endpoint webhook per produzione</h2>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-3 h-3 text-[#737373]" />
            <input
              value={prodDomain}
              onChange={(e) => setProdDomain(e.target.value)}
              onBlur={() => loadWebhooks(prodDomain)}
              onKeyDown={(e) => e.key === "Enter" && loadWebhooks(prodDomain)}
              placeholder="autofattura.dasabo.com"
              className="px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-mono w-56"
              data-testid="sys-webhook-domain-input"
            />
            <button onClick={() => loadWebhooks(prodDomain)}
              className="px-3 py-1.5 rounded-md bg-[#0A0A0A] text-white text-xs font-bold"
              data-testid="sys-webhook-domain-apply">
              Applica
            </button>
          </div>
        </div>
        <p className="px-5 py-3 text-xs text-[#737373] bg-[#FAF9F6] border-b border-[#E8E4D9]">
          Copia questi URL nelle console dei rispettivi provider quando fai il go-live. Base URL: <span className="font-mono text-[#0A0A0A]">{webhooks?.base_url}</span>
        </p>
        <div className="divide-y divide-[#E8E4D9]">
          {(webhooks?.webhooks || []).map((wh) => (
            <WebhookRow key={wh.id} webhook={wh} />
          ))}
        </div>
      </div>

      {/* PROVIDER KEYS SOURCE */}
      {providerKeys && providerKeys.length > 0 && (
        <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden" data-testid="sys-provider-keys">
          <div className="px-5 py-4 border-b border-[#E8E4D9]">
            <h2 className="font-display font-bold text-lg">Sorgente chiavi provider</h2>
            <p className="text-xs text-[#737373] mt-0.5">In produzione le chiavi dovrebbero stare tutte in DB (Fernet-cifrate, ruotabili senza restart). L&apos;env è solo bootstrap.</p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {providerKeys.map((pk) => {
                const sev = pk.severity;
                const rowBg = sev === "danger" ? "bg-red-50" : sev === "info" ? "bg-amber-50/40" : "";
                const srcBadge = pk.source === "db"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                  : pk.source === "env"
                    ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-red-100 text-red-800 border-red-300";
                return (
                  <tr key={pk.key} className={`border-b border-[#E8E4D9]/70 last:border-b-0 ${rowBg}`} data-testid={`sys-provider-key-${pk.key}`}>
                    <td className="px-4 py-3 text-xs font-bold w-64 align-top">
                      <div className="text-[#0A0A0A]">{pk.label}</div>
                      <div className="text-[10px] text-[#737373] font-mono normal-case tracking-normal mt-0.5">{pk.key}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${srcBadge}`}>
                        {pk.source === "db" ? "DB vault" : pk.source === "env" ? ".env" : "MANCANTE"}
                      </span>
                      {pk.warning && (
                        <div className={`mt-1 text-[11px] font-sans ${sev === "danger" ? "text-red-700 font-medium" : "text-amber-700"}`} data-testid={`sys-provider-key-${pk.key}-warning`}>
                          ⚠ {pk.warning}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 w-16 text-right pr-4 align-top">
                      {sev === "danger" ? <XCircle className="w-3.5 h-3.5 text-red-600 inline" />
                        : sev === "info" ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ENV DIAGNOSTICS */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden" data-testid="sys-env-diag">
        <div className="px-5 py-4 border-b border-[#E8E4D9] flex items-center gap-2">
          <Server className="w-4 h-4 text-[#0A0A0A]" />
          <h2 className="font-display font-bold text-lg">Diagnostica ambiente runtime</h2>
        </div>
        {envEffective && (
          <div className="px-5 py-4 bg-[#F7F5EE] border-b border-[#E8E4D9] text-xs space-y-2" data-testid="sys-env-effective">
            <div className="font-bold text-[#0A0A0A] uppercase tracking-wider text-[10px]">Configurazione effettiva applicata a runtime</div>
            <div className="grid md:grid-cols-3 gap-x-6 gap-y-2 font-mono">
              <div><span className="text-[#737373]">app_url →</span> <span className="text-[#0A0A0A]">{envEffective.app_url}</span></div>
              <div><span className="text-[#737373]">fic_redirect_uri →</span> <span className="text-[#0A0A0A] break-all">{envEffective.fic_redirect_uri}</span></div>
              <div><span className="text-[#737373]">stripe_mode →</span> <span className={envEffective.stripe_mode === "live" ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>{envEffective.stripe_mode || "n/d"}</span></div>
            </div>
            <div className="text-[#737373] font-sans">L&apos;app auto-corregge APP_FRONTEND_URL / FIC_REDIRECT_URI / STRIPE_MODE quando incoerenti con l&apos;ambiente. Aggiorna comunque le env per pulizia.</div>
          </div>
        )}
        <table className="w-full text-sm">
          <tbody>
            {envDiag && Object.entries(envDiag).map(([key, v]) => {
              const sev = v.warning ? (v.severity || "warning") : null;
              const rowBg = sev === "danger" ? "bg-red-50" : sev === "warning" ? "bg-amber-50" : "";
              return (
                <tr key={key} className={`border-b border-[#E8E4D9]/70 last:border-b-0 ${rowBg}`} data-testid={`sys-env-${key}`}>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#737373] font-bold w-64 font-mono align-top">{key}</td>
                  <td className={`px-4 py-3 font-mono text-xs break-all align-top ${v.set ? "text-[#0A0A0A]" : "text-red-700"}`}>
                    <div>{v.set ? (v.masked || v.value) : "NON IMPOSTATA"}</div>
                    {v.warning && (
                      <div
                        className={`mt-1 text-[11px] font-sans font-medium normal-case tracking-normal ${sev === "danger" ? "text-red-700" : "text-amber-700"}`}
                        data-testid={`sys-env-${key}-warning`}
                      >
                        ⚠ {v.warning}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 w-16 text-right pr-4 align-top">
                    {sev === "danger" ? (
                      <XCircle className="w-3.5 h-3.5 text-red-600 inline" />
                    ) : sev === "warning" ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline" />
                    ) : v.set ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-600 inline" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* AMBIENTE RUNTIME originale (OpenAPI mode, resend, webhook secret) */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E8E4D9]">
          <h2 className="font-display font-bold text-lg">Configurazione runtime</h2>
          <p className="text-xs text-[#737373] mt-0.5">Alcuni valori richiedono modifica di <span className="font-mono">backend/.env</span> + restart.</p>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <ConfRow label="OpenAPI mode" value={conf.mode} highlight={conf.mode === "production" ? "success" : "warning"} />
            <ConfRow label="OpenAPI endpoint" value={conf.endpoint} />
            <ConfRow label="OpenAPI email" value={conf.email} />
            <ConfRow label="Webhook secret" value={conf.webhook_secret_set ? "configurato" : "MANCANTE"} highlight={conf.webhook_secret_set ? "success" : "danger"} />
            <ConfRow label="Resend API" value={conf.resend_configured ? "configurato" : "MANCANTE"} highlight={conf.resend_configured ? "success" : "danger"} />
            <ConfRow label="URL pubblico" value={conf.public_url} />
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirmClear}
        onOpenChange={(v) => !v && setConfirmClear(null)}
        onConfirm={() => confirmClear && clearKey(confirmClear.key)}
        icon={Trash2}
        tone="danger"
        title={`Rimuovere ${confirmClear?.label} dal DB?`}
        description={
          confirmClear?.env_fallback
            ? `La chiave verrà rimossa dal DB e la piattaforma userà il fallback da .env (${confirmClear.env_fallback}). Nessun restart richiesto.`
            : "Nessun fallback .env disponibile per questo slot. Se procedi, il servizio corrispondente smetterà di funzionare finché non salvi una nuova chiave."
        }
        confirmLabel="Sì, rimuovi"
      />
    </div>
  );
}

function StatDot({ color, label, testid }) {
  const cls = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  }[color] || "bg-zinc-400";
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <span className={`w-2 h-2 rounded-full ${cls}`} />
      <span className="text-[#4A4A4A]">{label}</span>
    </div>
  );
}

function OpenApiTokenCard({ mode }) {
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/system/openapi/token-status");
      setStatus(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore stato token"); }
  };
  useEffect(() => { load(); }, []);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/system/openapi/force-refresh");
      toast.success(`Token rigenerato · valido ${(data.ttl_seconds / 86400).toFixed(1)}g`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rigenerazione fallita"); }
    finally { setRefreshing(false); }
  };

  const fmtDur = (s) => {
    if (!s || s < 0) return "—";
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
    return `${(s / 86400).toFixed(1)}g`;
  };
  const modeClr = mode === "production" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800";

  return (
    <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="sys-openapi-token">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Cable className="w-4 h-4 text-[#0A0A0A]" />
            <h2 className="font-display font-bold text-lg">Token OpenAPI SDI</h2>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${modeClr}`}>
              {mode}
            </span>
          </div>
          <p className="text-xs text-[#737373] max-w-2xl">
            Bearer JWT auto-gestito: cache persistente Fernet-cifrata in Mongo (shared multi-pod)
            + retry automatico su 401 con re-mint trasparente. L&apos;utente finale non vede mai errori
            di token scaduto sulle chiamate SDI.
          </p>
        </div>
        <button onClick={forceRefresh} disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0A0A0A] text-white text-xs font-bold disabled:opacity-40"
          data-testid="sys-openapi-force-refresh">
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Rigenera token adesso
        </button>
      </div>
      {status?.cached ? (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <TokenStat label="Mintato" value={new Date(status.minted_at * 1000).toLocaleString("it-IT")} />
          <TokenStat label="Età" value={fmtDur(status.age_seconds)} />
          <TokenStat label="Scade fra" value={fmtDur(status.ttl_seconds)} tone={status.ttl_seconds < 86400 ? "warn" : "ok"} />
          <TokenStat label="Scadenza" value={new Date(status.expire_ts * 1000).toLocaleString("it-IT")} />
        </div>
      ) : (
        <div className="mt-4 text-xs text-[#737373] italic" data-testid="sys-openapi-empty">
          Nessun token in cache. Verrà mintato automaticamente alla prima chiamata SDI.
        </div>
      )}
    </div>
  );
}

function TokenStat({ label, value, tone }) {
  const clr = tone === "warn" ? "text-amber-800" : tone === "ok" ? "text-emerald-800" : "text-[#0A0A0A]";
  return (
    <div className="rounded-lg bg-[#F5F2EA] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold">{label}</div>
      <div className={`text-sm font-mono font-semibold mt-0.5 ${clr}`}>{value}</div>
    </div>
  );
}

function WebhookRow({ webhook: wh }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(wh.url);
      setCopied(true);
      toast.success("URL copiato");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copia fallita"); }
  };
  const methodCls = wh.method === "GET" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800";
  return (
    <div className="px-5 py-4" data-testid={`sys-webhook-${wh.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${methodCls}`}>{wh.method}</span>
            <div className="font-display font-bold text-sm">{wh.label}</div>
            {wh.secret_slot && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#F5F2EA] text-[#4A4A4A]" title="Slot chiave associata">
                {wh.secret_slot}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 rounded-md bg-[#0A0A0A] px-3 py-2 max-w-full">
            <code className="font-mono text-xs text-emerald-300 truncate flex-1" data-testid={`sys-webhook-${wh.id}-url`}>{wh.url}</code>
            <button onClick={doCopy}
              className="text-white hover:text-emerald-300 shrink-0"
              title="Copia URL"
              data-testid={`sys-webhook-${wh.id}-copy`}>
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#737373]">
            <div>
              <span className="font-semibold">Eventi:</span>{" "}
              <span className="font-mono">{(wh.events || []).join(", ")}</span>
            </div>
            {wh.signed_header && (
              <div>
                <span className="font-semibold">Firma header:</span>{" "}
                <span className="font-mono">{wh.signed_header}</span>
              </div>
            )}
            <div className="md:col-span-2">
              <span className="font-semibold">Configura in:</span>{" "}
              {wh.configure_at?.startsWith("http") ? (
                <a href={wh.configure_at} target="_blank" rel="noreferrer" className="text-[#1D4ED8] hover:underline inline-flex items-center gap-1">
                  {wh.configure_at} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span>{wh.configure_at}</span>
              )}
            </div>
            {wh.notes && (
              <div className="md:col-span-2 italic mt-1">{wh.notes}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemSlotRow({ slot, busy, isEditing, onEdit, onCancel, onSave, onClear, onTest }) {
  const [val, setVal] = useState("");
  const [showPw, setShowPw] = useState(false);
  const source = slot.source;
  const badge = source === "db" ? { text: "DB", cls: "bg-emerald-100 text-emerald-800" }
              : source === "env" ? { text: "ENV", cls: "bg-amber-100 text-amber-800" }
              : { text: "MANCANTE", cls: "bg-red-100 text-red-800" };
  const testDot = slot.last_test_ok === true ? "bg-emerald-500"
                : slot.last_test_ok === false ? "bg-red-500"
                : "bg-zinc-300";
  const updatedRel = slot.updated_at ? new Date(slot.updated_at).toLocaleString("it-IT") : null;
  return (
    <div className="px-5 py-4" data-testid={`sys-slot-${slot.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block w-2 h-2 rounded-full ${testDot}`} title={slot.last_test_msg || "Mai testato"} />
            <div className="font-display font-semibold text-sm text-[#0A0A0A]">{slot.label}</div>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
            {slot.env_fallback && <span className="text-[10px] text-[#737373] font-mono">fallback: {slot.env_fallback}</span>}
          </div>
          <div className="text-xs text-[#737373] mt-1">{slot.description}</div>
          <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
            <span className="font-mono text-[#0A0A0A]">{slot.masked || "—"}</span>
            {updatedRel && <span className="text-[#737373]">• aggiornata: {updatedRel}</span>}
            {slot.last_tested_at && (
              <span className="text-[#737373]">
                • ultimo test: {new Date(slot.last_tested_at).toLocaleString("it-IT")}
                {slot.last_test_msg && <span className="ml-2 italic">— {slot.last_test_msg.slice(0, 120)}</span>}
              </span>
            )}
          </div>
          {isEditing && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <input
                  type={showPw ? "text" : "password"}
                  autoFocus
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder="Nuovo valore (viene cifrato Fernet)"
                  className="w-full pl-3 pr-9 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                  data-testid={`sys-slot-${slot.key}-input`}
                />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                  data-testid={`sys-slot-${slot.key}-toggle-pw`}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                disabled={busy || !val}
                onClick={() => onSave(val)}
                className="px-3 py-2 rounded-md bg-[#0A0A0A] text-white text-xs font-bold disabled:opacity-40"
                data-testid={`sys-slot-${slot.key}-save`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salva"}
              </button>
              <button
                onClick={() => { setVal(""); onCancel(); }}
                className="px-3 py-2 rounded-md border border-[#E8E4D9] text-xs"
                data-testid={`sys-slot-${slot.key}-cancel`}
              >
                Annulla
              </button>
            </div>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-2 shrink-0">
            {slot.test_provider && (
              <button
                disabled={busy || !slot.is_set}
                onClick={onTest}
                className="px-3 py-1.5 rounded-md border border-[#E8E4D9] text-xs font-bold text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid={`sys-slot-${slot.key}-test`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Test
              </button>
            )}
            <button
              onClick={onEdit}
              className="px-3 py-1.5 rounded-md bg-[#0A0A0A] text-white text-xs font-bold inline-flex items-center gap-1.5"
              data-testid={`sys-slot-${slot.key}-edit`}
            >
              <Pencil className="w-3 h-3" /> Modifica
            </button>
            {source === "db" && (
              <button
                disabled={busy}
                onClick={onClear}
                title="Rimuovi dal DB (userà fallback env se disponibile)"
                className="p-1.5 rounded-md border border-[#E8E4D9] text-red-700 hover:bg-red-50 disabled:opacity-40"
                data-testid={`sys-slot-${slot.key}-clear`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfRow({ label, value, highlight }) {
  const color = highlight === "success" ? "text-emerald-700" : highlight === "warning" ? "text-amber-700" : highlight === "danger" ? "text-red-700" : "text-[#0A0A0A]";
  return (
    <tr className="border-b border-[#E8E4D9]/70">
      <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#737373] font-bold w-56">{label}</td>
      <td className={`px-4 py-3 font-mono text-sm ${color}`}>{value || "—"}</td>
    </tr>
  );
}

function DiskUsagePanel({ diskUsage, cronLastRun, cleanupBusy, onRefresh, onCleanupPdfs, onCleanupLogs, onPurgeEvents }) {
  const [confirmPdf, setConfirmPdf] = useState(false);
  const fs = diskUsage.filesystem || {};
  const app = diskUsage.app_storage || {};
  const logs = diskUsage.supervisor_logs || {};
  const ec = diskUsage.event_collections || {};
  const criticalFs = fs.used_pct >= 80;
  const warnFs = fs.used_pct >= 60 && !criticalFs;
  const barTone = criticalFs ? "bg-red-500" : warnFs ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="sys-disk-usage">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${criticalFs ? "bg-red-100" : warnFs ? "bg-amber-100" : "bg-emerald-100"}`}>
            <HardDrive className={`w-5 h-5 ${criticalFs ? "text-red-700" : warnFs ? "text-amber-700" : "text-emerald-700"}`} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">Utilizzo disco container</div>
            <div className="text-xs text-[#737373] mt-0.5">
              {fs.used_gb} GB usati / {fs.total_gb} GB totali · {fs.free_gb} GB liberi · provider storage: <span className="font-mono">{diskUsage.storage_provider}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold"
          data-testid="disk-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Ricarica
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-4 h-3 rounded-full bg-[#F5F2EA] overflow-hidden">
        <div className={`${barTone} h-full transition-all`} style={{ width: `${Math.min(100, fs.used_pct || 0)}%` }} data-testid="disk-bar" />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] font-mono text-[#737373]">
        <span data-testid="disk-pct">{fs.used_pct}% usato</span>
        <span>{fs.free_gb} GB liberi</span>
      </div>

      {criticalFs && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 leading-relaxed">
          ⚠️ <b>Disco oltre 80%.</b> Considera cleanup immediato (log/orphan PDF) e/o migrazione storage a Cloudflare R2 se non già attiva. Se il provider è ancora <span className="font-mono">local</span>, i file utente crescono sul volume K8s.
        </div>
      )}

      {/* Breakdown */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-[#E8E4D9] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Storage app</div>
          <div className="mt-1 font-display font-extrabold text-2xl tabular-nums">{app.total_mb} MB</div>
          <div className="text-[11px] text-[#737373] mt-0.5">{app.total_files} file su {(app.dirs || []).length} directory</div>
          <ul className="mt-2 space-y-1 text-[11px] font-mono">
            {(app.dirs || []).map((d) => (
              <li key={d.path} className="flex justify-between gap-2">
                <span className="truncate text-[#737373]">{d.path.split("/").pop()}</span>
                <span className="text-[#0A0A0A] shrink-0">{d.size_mb} MB · {d.file_count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-[#E8E4D9] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Log supervisor</div>
          <div className="mt-1 font-display font-extrabold text-2xl tabular-nums">{logs.total_mb} MB</div>
          <div className="text-[11px] text-[#737373] mt-0.5">Soglia rotate: {logs.rotate_threshold_mb} MB</div>
          <ul className="mt-2 space-y-1 text-[11px] font-mono">
            {(logs.files || []).slice(0, 5).map((f) => (
              <li key={f.name} className="flex justify-between gap-2">
                <span className="truncate text-[#737373]">{f.name}</span>
                <span className={`shrink-0 ${f.size_mb > logs.rotate_threshold_mb ? "text-red-700 font-bold" : "text-[#0A0A0A]"}`}>{f.size_mb} MB</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-[#E8E4D9] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Event log DB</div>
          <div className="mt-1 font-display font-extrabold text-2xl tabular-nums">{Object.values(ec).reduce((s, v) => s + (v || 0), 0)}</div>
          <div className="text-[11px] text-[#737373] mt-0.5">Righe totali · TTL Mongo attivo</div>
          <ul className="mt-2 space-y-1 text-[11px] font-mono">
            {Object.entries(ec).map(([k, v]) => (
              <li key={k} className="flex justify-between gap-2">
                <span className="truncate text-[#737373]">{k.replace("_events", "").replace("system_", "")}</span>
                <span className="text-[#0A0A0A] shrink-0">{v ?? "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 rounded-lg bg-[#FAF9F6] border border-[#E8E4D9] p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-bold">Manutenzione manuale</div>
            <div className="text-[11px] text-[#737373]">
              {diskUsage.orphan_source_pdfs !== null && (
                <>PDF sorgente orfani: <b>{diskUsage.orphan_source_pdfs}</b> · </>
              )}
              Auto cron: <span className="font-mono">15 3 * * *</span> UTC
              {cronLastRun?.finished_at && (
                <> · Ultimo run: {new Date(cronLastRun.finished_at).toLocaleString("it-IT")}</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onCleanupPdfs(true)}
              disabled={cleanupBusy === "pdfs"}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA] disabled:opacity-40"
              data-testid="disk-cleanup-pdfs-dry"
            >
              {cleanupBusy === "pdfs" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              Anteprima cleanup PDF
            </button>
            <button
              onClick={() => setConfirmPdf(true)}
              disabled={cleanupBusy === "pdfs" || diskUsage.orphan_source_pdfs === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-40"
              data-testid="disk-cleanup-pdfs-apply"
            >
              <Trash2 className="w-3 h-3" /> Cancella PDF orfani
            </button>
            <button
              onClick={() => onCleanupLogs(logs.rotate_threshold_mb)}
              disabled={cleanupBusy === "logs"}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA] disabled:opacity-40"
              data-testid="disk-cleanup-logs"
            >
              {cleanupBusy === "logs" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Ruota log ora
            </button>
            <button
              onClick={onPurgeEvents}
              disabled={cleanupBusy === "events"}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA] disabled:opacity-40"
              data-testid="disk-purge-events"
            >
              {cleanupBusy === "events" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
              Purge event log
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmPdf}
        onOpenChange={setConfirmPdf}
        onConfirm={() => { setConfirmPdf(false); onCleanupPdfs(false); }}
        icon={Trash2}
        tone="danger"
        title={`Eliminare ${diskUsage.orphan_source_pdfs || 0} PDF orfani?`}
        description="I PDF sorgente vengono eliminati SOLO per fatture che hanno già l'XML FatturaPA generato (source of truth fiscale). Operazione irreversibile ma non impatta l'archivio fiscale."
        confirmLabel="Sì, elimina PDF orfani"
      />
    </div>
  );
}

